import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Search, Plus, Clock, Sparkles, ImageOff } from "lucide-react";

function AddRecipeModal({ open, onClose, onCreated }) {
  const [mode, setMode] = useState("paste"); // "paste" | "manual"
  const [paste, setPaste] = useState("");
  const [parsing, setParsing] = useState(false);
  const [r, setR] = useState({ name: "", description: "", ingredients: "", instructions: "", yield_str: "", prep_time_minutes: "", tags: "" });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const parse = async () => {
    if (!paste.trim()) {
      toast.error("Paste the recipe text first");
      return;
    }
    setParsing(true);
    try {
      const { data } = await api.post("/admin/recipes/parse", { text: paste });
      setR({
        name: data.name || "",
        description: data.description || "",
        ingredients: (data.ingredients || []).join("\n"),
        instructions: (data.instructions || []).join("\n"),
        yield_str: data.yield_str || "",
        prep_time_minutes: data.prep_time_minutes || "",
        tags: (data.tags || []).join(", "),
      });
      setMode("manual");
      toast.success("Parsed — review and save");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Parse failed");
    } finally {
      setParsing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: r.name,
        description: r.description,
        ingredients: r.ingredients.split("\n").map((s) => s.trim()).filter(Boolean),
        instructions: r.instructions.split("\n").map((s) => s.trim()).filter(Boolean),
        yield_str: r.yield_str,
        prep_time_minutes: r.prep_time_minutes ? parseInt(r.prep_time_minutes) : null,
        tags: r.tags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/admin/recipes", payload);
      toast.success("Recipe added");
      onCreated(data);
      onClose();
      setPaste("");
      setR({ name: "", description: "", ingredients: "", instructions: "", yield_str: "", prep_time_minutes: "", tags: "" });
      setMode("paste");
    } catch (err) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--brand-ink))]/40 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl p-8 border border-[hsl(var(--brand-border))] shadow-xl max-h-[92vh] overflow-y-auto" data-testid="add-recipe-form">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display text-3xl">New recipe</div>
            <div className="text-sm text-[hsl(var(--brand-muted))] mt-1">Paste any recipe text and let AI parse it — or fill manually.</div>
          </div>
        </div>

        <div className="inline-flex bg-[hsl(var(--brand-cream-deep))] p-1 rounded-full mb-5">
          <button type="button" onClick={() => setMode("paste")} className={`px-4 py-1.5 rounded-full text-sm ${mode === "paste" ? "bg-white shadow-sm" : "text-[hsl(var(--brand-muted))]"}`} data-testid="recipe-mode-paste">
            Paste & parse
          </button>
          <button type="button" onClick={() => setMode("manual")} className={`px-4 py-1.5 rounded-full text-sm ${mode === "manual" ? "bg-white shadow-sm" : "text-[hsl(var(--brand-muted))]"}`} data-testid="recipe-mode-manual">
            Manual
          </button>
        </div>

        {mode === "paste" ? (
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Paste recipe text</label>
            <textarea
              rows={12}
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              placeholder="Paste from WhatsApp, a website, or anywhere — name, ingredients, method, etc."
              className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md text-sm"
              data-testid="recipe-paste-textarea"
            />
            <div className="flex gap-3 justify-end mt-4">
              <button type="button" onClick={onClose} className="px-5 py-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))]">Cancel</button>
              <button type="button" disabled={parsing} onClick={parse} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))] disabled:opacity-60" data-testid="recipe-parse-btn">
                <Sparkles className="w-4 h-4" /> {parsing ? "Parsing…" : "Parse with AI"}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { k: "name", label: "Name", required: true },
                { k: "yield_str", label: "Yield (e.g., 2 servings)" },
                { k: "prep_time_minutes", label: "Prep time (min)", type: "number" },
                { k: "tags", label: "Tags (comma separated)" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">{f.label}</label>
                  <input type={f.type || "text"} required={f.required} value={r[f.k]} onChange={(e) => setR({ ...r, [f.k]: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid={`recipe-field-${f.k}`} />
                </div>
              ))}
            </div>
            <div className="mt-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Description</label>
              <textarea rows={2} value={r.description} onChange={(e) => setR({ ...r, description: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="recipe-field-description" />
            </div>
            <div className="mt-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Ingredients (one per line)</label>
              <textarea rows={6} value={r.ingredients} onChange={(e) => setR({ ...r, ingredients: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md font-mono text-sm" data-testid="recipe-field-ingredients" />
            </div>
            <div className="mt-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Instructions (one step per line)</label>
              <textarea rows={6} value={r.instructions} onChange={(e) => setR({ ...r, instructions: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md text-sm" data-testid="recipe-field-instructions" />
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button type="button" onClick={onClose} className="px-5 py-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))]">Cancel</button>
              <button disabled={saving} className="px-5 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))] disabled:opacity-60" data-testid="recipe-submit-btn">
                {saving ? "Saving…" : "Save recipe"}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

function RecipeThumb({ r }) {
  const url = r.image_url
    ? (r.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${r.image_url}` : r.image_url)
    : "";
  if (!url) {
    return (
      <div className="w-full h-full grid place-items-center bg-[hsl(var(--brand-cream-deep))] text-[hsl(var(--brand-muted))]">
        <div className="text-center">
          <ImageOff className="w-8 h-8 mx-auto opacity-50" />
          <div className="text-[10px] tracking-[0.2em] uppercase mt-1 opacity-70">No photo</div>
        </div>
      </div>
    );
  }
  return <img src={url} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />;
}

export default function AdminRecipes() {
  const [recipes, setRecipes] = useState([]);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (tag) params.set("tag", tag);
    const [{ data: r }, { data: t }] = await Promise.all([
      api.get(`/admin/recipes?${params.toString()}`),
      api.get("/admin/recipes/tags"),
    ]);
    setRecipes(r);
    setTags(t);
    setLoading(false);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [search, tag]);

  return (
    <div data-testid="admin-recipes-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Recipe library</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">The kitchen</h1>
          <p className="text-[hsl(var(--brand-muted))] mt-2">{recipes.length} recipes · attach any to a meal slot.</p>
        </div>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]" data-testid="add-recipe-btn">
          <Plus className="w-4 h-4" /> New recipe
        </button>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--brand-muted))]" />
          <input placeholder="Search recipes…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 border border-[hsl(var(--brand-border))] rounded-md bg-white" data-testid="recipes-search-input" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setTag("")} className={`px-3 py-1.5 text-xs rounded-full border ${tag === "" ? "bg-[hsl(var(--brand-sage))] text-white border-[hsl(var(--brand-sage))]" : "bg-white border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream-deep))]"}`} data-testid="tag-filter-all">All</button>
          {tags.map((t) => (
            <button key={t} onClick={() => setTag(t)} className={`px-3 py-1.5 text-xs rounded-full border capitalize ${tag === t ? "bg-[hsl(var(--brand-sage))] text-white border-[hsl(var(--brand-sage))]" : "bg-white border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream-deep))]"}`} data-testid={`tag-filter-${t}`}>{t}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-10 font-serif-italic text-[hsl(var(--brand-muted))]">Loading…</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {recipes.map((r) => (
            <Link key={r.id} to={`/admin/recipes/${r.id}`} className="group bg-white border border-[hsl(var(--brand-border))] rounded-xl overflow-hidden card-lift" data-testid={`recipe-card-${r.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-[hsl(var(--brand-cream-deep))]">
                <RecipeThumb r={r} />
              </div>
              <div className="p-4">
                <div className="font-display text-lg leading-tight">{r.name}</div>
                <div className="flex items-center gap-3 mt-2 text-xs text-[hsl(var(--brand-muted))]">
                  {r.prep_time_minutes ? (<span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{r.prep_time_minutes}m</span>) : null}
                  <span>{r.ingredients?.length || 0} ingredients</span>
                </div>
                {r.tags?.length ? (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {r.tags.slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(var(--brand-cream-deep))] text-[hsl(var(--brand-muted))]">{t}</span>
                    ))}
                  </div>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      )}

      <AddRecipeModal open={open} onClose={() => setOpen(false)} onCreated={() => load()} />
    </div>
  );
}
